/**
 * Current price and forward scenario bands for one company.
 *
 * Reads the most recent forecast set if one exists and offers to generate a
 * fresh one. The 1-month horizon is shown first because it is the one most
 * people ask for; the longer horizons follow in the same table. Scenario
 * ranges come from the stock's own historical volatility and drift, so they
 * describe a plausible range rather than a prediction.
 */

import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { ForecastSet, PriceForecast } from "@/types";
import ProbabilityBar from "@/components/research/ProbabilityBar";

const num = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const money = (value: string | number | null | undefined): string => {
  const parsed = num(value);
  return parsed === null ? "—" : `$${parsed.toFixed(2)}`;
};

const pct = (value: string | number | null | undefined): string => {
  const parsed = num(value);
  return parsed === null ? "—" : `${parsed >= 0 ? "+" : ""}${parsed.toFixed(2)}%`;
};

const tone = (value: string | number | null | undefined): string => {
  const parsed = num(value);
  if (parsed === null) return "text-mist-400";
  if (parsed > 0) return "text-emerald-400";
  if (parsed < 0) return "text-rose-400";
  return "text-mist-300";
};

export default function PriceOutlookPanel({
  companyId,
  currentPrice,
}: {
  companyId: string;
  currentPrice?: number | null;
}): React.ReactElement {
  const [forecastSet, setForecastSet] = useState<ForecastSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const { data } = await apiClient.get<ForecastSet>(`/companies/${companyId}/forecast/latest`);
      setForecastSet(data);
    } catch {
      setForecastSet(null);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = async (): Promise<void> => {
    setIsGenerating(true);
    setMessage(null);
    try {
      const { data } = await apiClient.post<ForecastSet>(`/companies/${companyId}/forecast`);
      setForecastSet(data);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMessage(
        detail
          ? String(detail)
          : "Couldn't generate an outlook. At least 60 days of price history are needed.",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const horizons: PriceForecast[] = forecastSet?.forecasts ?? [];
  const oneMonth = horizons.find((h) => h.horizon_label === "1M") ?? null;
  const reference = num(forecastSet?.reference_price) ?? currentPrice ?? null;

  return (
    <div className="panel p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-mist-100">Price &amp; outlook</h2>
          <p className="mt-1 text-sm text-mist-400">
            Latest close and forward scenario ranges from the forecast engine.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void generate()}
          disabled={isGenerating}
          className="rounded-lg border border-ink-600 bg-ink-800 px-4 py-2 text-sm font-medium text-mist-100 transition hover:border-ink-500 disabled:opacity-60"
        >
          {isGenerating ? "Generating…" : forecastSet ? "Refresh outlook" : "Generate outlook"}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3 stagger-children">
        <div className="animate-rise rounded-lg border border-ink-700 bg-ink-900/60 p-4">
          <p className="text-xs uppercase tracking-wide text-mist-400">Current price</p>
          <p className="mt-1 text-2xl font-semibold text-mist-100">{money(reference)}</p>
        </div>
        <div className="animate-rise rounded-lg border border-ink-700 bg-ink-900/60 p-4">
          <p className="text-xs uppercase tracking-wide text-mist-400">Expected in 1 month</p>
          <p className="mt-1 text-2xl font-semibold text-mist-100">
            {oneMonth ? money(oneMonth.expected_price) : "—"}
          </p>
          {oneMonth && (
            <p className={`text-xs ${tone(oneMonth.expected_return_pct)}`}>
              {pct(oneMonth.expected_return_pct)} vs today
            </p>
          )}
        </div>
        <div className="animate-rise rounded-lg border border-ink-700 bg-ink-900/60 p-4">
          <p className="text-xs uppercase tracking-wide text-mist-400">1-month range</p>
          <p className="mt-1 text-lg font-semibold text-mist-100">
            {oneMonth ? `${money(oneMonth.bear_price)} – ${money(oneMonth.bull_price)}` : "—"}
          </p>
          {oneMonth && (
            <p className="text-xs text-mist-400">
              confidence {(num(oneMonth.confidence) ?? 0).toFixed(2)} · volatility{" "}
              {(num(oneMonth.volatility_annualized_pct) ?? 0).toFixed(0)}%
            </p>
          )}
        </div>
      </div>

      {/* 1-month probability distribution */}
      {oneMonth && (
        <div className="mt-4 rounded-lg border border-ink-700 bg-ink-900/40 p-4">
          <p className="mb-2 text-xs uppercase tracking-wide text-mist-400">
            1-month probability distribution
          </p>
          <ProbabilityBar
            bearPct={num(oneMonth.bear_return_pct) ?? 0}
            basePct={num(oneMonth.base_return_pct) ?? 0}
            bullPct={num(oneMonth.bull_return_pct) ?? 0}
            expectedPct={num(oneMonth.expected_return_pct) ?? 0}
          />
        </div>
      )}

      {isLoading && <p className="mt-4 text-sm text-mist-400">Loading outlook…</p>}
      {message && <p className="mt-4 text-sm text-rose-400">{message}</p>}

      {!isLoading && !forecastSet && !message && (
        <p className="mt-4 text-sm text-mist-400">
          No outlook generated yet. Use “Generate outlook” once at least 60 days of price history
          have loaded.
        </p>
      )}

      {horizons.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead className="border-b border-ink-700 text-xs uppercase tracking-wide text-mist-400">
              <tr>
                <th className="px-3 py-2">Horizon</th>
                <th className="px-3 py-2">Bear</th>
                <th className="px-3 py-2">Base</th>
                <th className="px-3 py-2">Bull</th>
                <th className="px-3 py-2">Expected</th>
              </tr>
            </thead>
            <tbody>
              {horizons.map((horizon) => (
                <tr key={horizon.id} className="border-b border-ink-800 last:border-0">
                  <td className="px-3 py-2 font-medium text-mist-100">{horizon.horizon_label}</td>
                  <td className="px-3 py-2 text-rose-300">{money(horizon.bear_price)}</td>
                  <td className="px-3 py-2 text-mist-200">{money(horizon.base_price)}</td>
                  <td className="px-3 py-2 text-emerald-300">{money(horizon.bull_price)}</td>
                  <td className="px-3 py-2">
                    <span className="font-medium text-mist-100">{money(horizon.expected_price)}</span>{" "}
                    <span className={`text-xs ${tone(horizon.expected_return_pct)}`}>
                      {pct(horizon.expected_return_pct)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {forecastSet?.disclaimer && (
        <p className="mt-4 text-xs leading-relaxed text-mist-500">{forecastSet.disclaimer}</p>
      )}
    </div>
  );
}
